import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Skeleton,
} from "@mui/material";
import {
  Zap,          // express
  Rocket,       // fast
  Leaf,         // eco
  Truck,        // header icon
  Clock         // custom (added for eco sub-options)
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { calculateRates } from "../../utils/calculateRates.js";

const OPTION_ICONS = {
  express: Zap,
  fast: Rocket,
  eco: Leaf,
  custom: Clock,   // add this
};

const formatEur = (n) => `€${Number(n).toFixed(2)}`;

export function DeliveryOptions({
  km,
  durationMin,
  items,
  originData,
  destData,
  value,
  onChange,
  defaultOptionId = "express",
  onEcoClick,
  pickupTime,
  targetTime,
  hasTimeWindow,
}) {
  const { t } = useTranslation();
  const distanceKm = Number(km || 0);
  const duration = Number(durationMin || 0);

  // ✅ Calculate all rates from calculateRates function
  const [options, setOptions] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);

  // ✅ Create a dependency key to track when addresses/items change
  const dependencyKey = useMemo(() => {
    return JSON.stringify({
      originLat: originData?.latitude,
      originLng: originData?.longitude,
      destLat: destData?.latitude,
      destLng: destData?.longitude,
      distance: distanceKm,
      itemsCount: items?.length || 0,
      itemsWeights: items?.map(i => i.weight).join(',') || '',
      pickupTime,
      targetTime,
      hasTimeWindow,
    });
  }, [originData, destData, distanceKm, items, pickupTime, targetTime, hasTimeWindow]);

  useEffect(() => {
    if (!items?.length || !originData?.latitude || !destData?.latitude || !distanceKm) {
      setOptions([]);
      setRatesLoading(false);
      return;
    }

    // ✅ Show loading immediately when dependencies change
    setRatesLoading(true);
    
    // ✅ Add a small delay to debounce rapid changes
    const timer = setTimeout(() => {
      try {

        const {rates} = calculateRates({
          km: distanceKm,
          durationMin: duration,
          items: items.map(item => ({
            weight: item.weight,
            dimensions: item.dimensions,
          })),
          origin: {
            latitude: originData.latitude,
            longitude: originData.longitude,
            address: originData.address,
            postal_code: originData.postal_code,
            placeId: originData.placeId,
          },
          destination: {
            latitude: destData.latitude,
            longitude: destData.longitude,
            address: destData.address,
            postal_code: destData.postal_code,
            placeId: destData.placeId,
          },
          pickupConfig: {
            earliestPickup: new Date().getTime(), // optional, we default to now
            latestPickup:  new Date().getTime() + 36000, // optional, we default to 2 hours from now
        
            // these user wont change
            cutoffTime: "5:00 PM", // we have set this to 5 PM for testing purposes
            enabledDays: { // 
              monday: true,
              tuesday: true,
              wednesday: true,
              thursday: true,
              friday: true,
              saturday: true,
              sunday: true,
            },
          },
          currency: "EUR",
          enabledOptions: ["FD", "SD"],
          pickupTime,
          targetTime,
          hasTimeWindow,
        });

        setOptions(rates);
      } catch (error) {
        console.error("Failed to calculate rates:", error);
        setOptions([]);
      } finally {
        setRatesLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [dependencyKey]); // ✅ Use the dependency key instead of individual deps

  const ready = Number.isFinite(distanceKm) && distanceKm > 0;

  const [selectedId, setSelectedId] = useState(value?.optionId || null);

  useEffect(() => {
    if (value?.optionId !== undefined) {
      // If it's an eco sub-option (eco-bid, eco-bus, eco-regular), show eco as selected
      const optionId = value.optionId;
      if (optionId && optionId.startsWith('eco-')) {
        setSelectedId('eco');
      } else {
        setSelectedId(optionId);
      }
    }
  }, [value?.optionId]);

  const emitChange = (opt) => {
    onChange?.({
      optionId: opt.id,
      price: opt.price,
      option: opt,
    });
  };

  const select = (opt) => {
    // ✅ Eco option - opens modal with bus/regular/bid
    if (opt.id === "eco") {
      if (onEcoClick) {
        onEcoClick(opt);
      }
      return;
    }

    // Express or Fast - direct selection
    setSelectedId(opt.id);
    emitChange(opt);
  };

  useEffect(() => {
    if (!ready) return;
    if (selectedId) return;
    if (options.length === 0) return;

    const preferred = options.find((o) => o.id === defaultOptionId) || options[0];
    if (!preferred) return;

    setSelectedId(preferred.id);
    emitChange(preferred);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, options, defaultOptionId]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Truck size={18} />
          <Typography variant="subtitle2" fontWeight={600}>
            {t("delivery.deliveryOptions")}
          </Typography>
        </Box>
        
        {!ready && (
          <Typography variant="caption" color="text.secondary">
            {t("delivery.enterAddressesPrompt") || "Enter pickup and delivery addresses to see options"}
          </Typography>
        )}

        {/* ✅ Show loading indicator when calculating rates */}
        {ready && ratesLoading && (
          <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
            {t("delivery.calculatingRates") || "Calculating rates..."}
          </Typography>
        )}
      </Box>

      {/* Options Stack */}
      <Stack spacing={1.5}>
        {!ready ? (
          // Not ready - show placeholder message
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                {t("delivery.selectAddressesToSeeOptions")}
              </Typography>
            </CardContent>
          </Card>
        ) : ratesLoading ? (
          // ✅ Loading state - show skeletons
          <>
            {[1, 2, 3].map((i) => (
              <Card key={i} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: 1.25, flex: 1 }}>
                      <Skeleton variant="circular" width={34} height={34} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="40%" height={24} />
                        <Skeleton variant="text" width="70%" height={20} />
                      </Box>
                    </Box>
                    <Skeleton variant="text" width={60} height={24} />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </>
        ) : options.length === 0 ? (
          // ✅ No options available
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                {t("delivery.noOptionsAvailable")}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          // ✅ Show options
          options.map((opt) => {
            const isSelected = selectedId === opt.id;
            const Icon = OPTION_ICONS[opt.id] || Rocket;
            const showStartingFrom = opt.priceType === "startingFrom";

            // Check if eco sub-option is selected
            let ecoSubOptionLabel = null;
            if (opt.id === "eco" && value?.optionId?.startsWith("eco-")) {
              const subOptionMap = {
                "eco-bus": t("delivery.busTransport") || "Bus Transport",
                "eco-regular": t("delivery.regular") || "Regular",
                "eco-bid": t("delivery.bid") || "Bid"
              };
              ecoSubOptionLabel = subOptionMap[value.optionId];
            }

            return (
              <Card
                key={opt.id}
                onClick={() => select(opt)}
                sx={{
                  cursor: "pointer",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isSelected ? "primary.main" : "divider",
                  boxShadow: isSelected ? 3 : 0,
                  transition: "all 0.15s ease",
                  '&:hover': {
                    borderColor: isSelected ? "primary.main" : "primary.light",
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  }
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    <Box sx={{ display: "flex", gap: 1.25, minWidth: 0 }}>
                      <Box
                        sx={{
                          mt: "2px",
                          width: 34,
                          height: 34,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: isSelected ? "primary.50" : "action.hover",
                        }}
                      >
                        <Icon size={18} />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontWeight: 900 }}>
                            {opt.title}
                          </Typography>
                          {ecoSubOptionLabel && (
                            <Box
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: 'primary.main',
                                color: 'black',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                              }}
                            >
                              {ecoSubOptionLabel}
                            </Box>
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                          {opt.description}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ textAlign: "right", minWidth: 30 }}>
                      {showStartingFrom && !ecoSubOptionLabel ? (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                            {t("delivery.startingFrom") || "Starting from"}
                          </Typography>
                          <Typography sx={{ fontWeight: 900 }}>
                            {formatEur(opt.price ?? 0)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography sx={{ fontWeight: 900 }}>
                          {formatEur(ecoSubOptionLabel ? (value?.price ?? opt.price ?? 0) : (opt.price ?? 0))}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })
        )}
      </Stack>
    </Box>
  );
}